class CartDrawerV2 extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('.cart-drawer-v2-overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink?.setAttribute('role', 'button');
    cartLink?.setAttribute('aria-haspopup', 'dialog');
    cartLink?.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink?.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => { this.classList.add('active'); });

    document.body.classList.add('drawer-open');

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.cart-drawer-v2-empty')
        : this.querySelector('.cart-drawer-v2-panel');
      const focusElement = this.querySelector('.cart-drawer-v2-panel') || this.querySelector('.cart-drawer-v2-close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    document.dispatchEvent(new CustomEvent('cartdrawer:open'));
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('drawer-open');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    const offersDrawer = this.querySelector('.cart-drawer-v2-offers-drawer');
    const offersOverlay = this.querySelector('.cart-drawer-v2-offers-overlay');
    const copyBar = this.querySelector('[data-action="open-offers-drawer"]');
    if (offersDrawer) offersDrawer.classList.remove('is-open');
    if (offersOverlay) offersOverlay.classList.remove('is-open');
    if (copyBar) copyBar.style.display = '';

    const giftModal = this.querySelector('[data-gift-modal]');
    if (giftModal) giftModal.setAttribute('aria-hidden', 'true');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.cart-drawer-v2-panel')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    setTimeout(() => {
      this.querySelector('.cart-drawer-v2-overlay')?.addEventListener('click', this.close.bind(this));

      // Every cart mutation path lands here - including the legacy gift wrap
      // sync in custom.js, which bundles only the drawer's sections and so left
      // /cart showing a stale wrap quantity until a reload. On /cart the page
      // itself shows the cart, so refresh it instead of sliding the drawer open.
      if (_v2OnCartPage()) {
        _v2RefreshCartPage().catch((err) => console.error('V2 cart page refresh error:', err));
        return;
      }

      this.open();
    });

    document.dispatchEvent(new CustomEvent('cartChange'));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer-v2',
        selector: '#CartDrawerV2'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer-v2', CartDrawerV2);

document.addEventListener('click', (e) => {
  const copyBar = e.target.closest('[data-action="open-offers-drawer"]');
  if (copyBar) {
    const panel = copyBar.closest('.cart-drawer-v2-panel');
    const drawer = panel.querySelector('.cart-drawer-v2-offers-drawer');
    const overlay = panel.querySelector('.cart-drawer-v2-offers-overlay');
    if (drawer) {
      drawer.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
      copyBar.style.display = 'none';
    }
    return;
  }

  const closeBtn = e.target.closest('.cart-drawer-v2-offers-close');
  const overlayClick = e.target.classList.contains('cart-drawer-v2-offers-overlay') ? e.target : null;
  if (closeBtn || overlayClick) {
    const panel = (closeBtn || overlayClick).closest('.cart-drawer-v2-panel');
    const drawer = panel.querySelector('.cart-drawer-v2-offers-drawer');
    const overlay = panel.querySelector('.cart-drawer-v2-offers-overlay');
    const bar = panel.querySelector('[data-action="open-offers-drawer"]');
    if (drawer) drawer.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    if (bar) bar.style.display = '';
    return;
  }

  const copyBtn = e.target.closest('.cart-drawer-v2-offer-copy-btn');
  if (copyBtn) {
    const code = copyBtn.getAttribute('data-coupon-code');
    if (code && navigator.clipboard) {
      navigator.clipboard.writeText(code).then(() => {
        const originalHTML = copyBtn.innerHTML;
        copyBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 6L9 17L4 12" stroke="#22c55e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        setTimeout(() => { copyBtn.innerHTML = originalHTML; }, 1500);
      });
    }
    return;
  }
});

document.addEventListener('click', (e) => {
  const quantityBtn = e.target.closest('.cart-drawer-v2-quantity-btn');
  if (!quantityBtn) return;

  const container = quantityBtn.closest('.cart-drawer-v2-quantity');
  if (!container) return;

  const input = container.querySelector('.cart-drawer-v2-quantity-input');
  if (!input) return;

  const action = quantityBtn.dataset.action;
  const name = quantityBtn.getAttribute('name');
  const currentValue = parseInt(input.value) || 1;
  const step = parseInt(input.getAttribute('step')) || 1;
  const max = input.getAttribute('max') ? parseInt(input.getAttribute('max')) : null;

  if (action === 'remove') {
    input.value = 0;
  } else if (action === 'decrement' || name === 'minus') {
    const newVal = currentValue - step;
    input.value = newVal < 0 ? 0 : newVal;
  } else if (name === 'plus') {
    const newVal = currentValue + step;
    if (max && newVal > max) return;
    input.value = newVal;
  }

  input.dispatchEvent(new Event('change', { bubbles: true }));
});

let _v2GiftBusy = false;

const _v2IsWrapLine = (item) => Boolean(item.properties && item.properties['_gift_hide_quantity']);

const _v2OnCartPage = () => document.getElementById('main-cart-items') !== null;

let _v2GiftTypeMapCache = {};

// /cart.js carries no product tags, so paid/free can only come from the rendered
// rows. Section re-renders destroy those rows mid-flight, so merge into a cache
// rather than replacing it, and always call this synchronously before any await.
function _v2GetGiftTypeMap() {
  document.querySelectorAll('[data-item-key]').forEach((el) => {
    _v2GiftTypeMapCache[el.dataset.itemKey] = el.dataset.giftType || '';
  });
  return _v2GiftTypeMapCache;
}

// The cart page sections only exist in templates/cart.json, so their ids come
// from the DOM (cart-items / cart-footer), never from the element id.
const _v2CartPageTargets = () => {
  const targets = [];
  ['main-cart-items', 'main-cart-footer'].forEach((elementId) => {
    const host = document.getElementById(elementId);
    if (host && host.dataset.id) {
      targets.push({ elementId: elementId, section: host.dataset.id, selector: '.js-contents' });
    }
  });
  return targets;
};

// Section ids for the cart POST endpoints. Cart page sections are deliberately
// excluded: this store returns null for template-scoped ids on /cart/update.js
// and /cart/add.js (verified in-browser 2026-08-28), while the GET ?sections=
// path renders them correctly - so /cart is repainted by _v2RefreshCartPage.
const _v2MutationSectionIds = () => {
  const drawer = document.querySelector('cart-drawer-v2');
  return drawer ? drawer.getSectionsToRender().map((s) => s.section || s.id) : [];
};

const _v2AllSectionIds = () => {
  const drawer = document.querySelector('cart-drawer-v2');
  const ids = drawer ? drawer.getSectionsToRender().map((s) => s.section || s.id) : [];
  _v2CartPageTargets().forEach((target) => ids.push(target.section));
  return [...new Set(ids)];
};

const _v2SectionInner = (html, selector) => {
  if (typeof html !== 'string' || html === '') return null;
  const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  return node ? node.innerHTML : null;
};

// Paints every surface a cart mutation can affect without calling
// renderContents(), which opens the drawer — unwanted on /cart. A missing or
// null section degrades to "leave it alone" so one bad key cannot kill the run.
// Returns false when a cart page target was present but its section came back
// unusable, so the caller can fall back to a fresh render rather than silently
// leaving a stale quantity on screen.
const _v2Repaint = (sections) => {
  if (!sections) return false;

  const panel = document.querySelector('.cart-drawer-v2-panel');
  const panelHTML = _v2SectionInner(sections['cart-drawer-v2'], '.cart-drawer-v2-panel');
  if (panel && panelHTML !== null) panel.innerHTML = panelHTML;

  const bubble = document.getElementById('cart-icon-bubble');
  const bubbleHTML = _v2SectionInner(sections['cart-icon-bubble'], '.shopify-section');
  if (bubble && bubbleHTML !== null) bubble.innerHTML = bubbleHTML;

  let cartPagePainted = true;
  _v2CartPageTargets().forEach(({ elementId, section, selector }) => {
    const host = document.getElementById(elementId);
    if (!host) return;
    if (!(section in sections)) {
      cartPagePainted = false;
      return;
    }
    const inner = _v2SectionInner(sections[section], selector);
    if (inner === null) {
      console.warn('V2 gift wrap: section "' + section + '" came back empty, refetching.');
      cartPagePainted = false;
      return;
    }
    (host.querySelector(selector) || host).innerHTML = inner;
  });

  return cartPagePainted;
};

// The checkout control and the is-empty classes sit outside .js-contents, so no
// section swap ever updates them. Reachable case: dropping the last product
// leaves the wrap behind, so item_count is briefly non-zero before the reconcile
// zeroes it — without this the page keeps an enabled checkout on an empty cart.
const _v2SyncEmptyState = (cart) => {
  if (!cart) return;

  const items = Array.isArray(cart.items) ? cart.items : [];
  const isEmpty = (cart.item_count || 0) === 0;
  const onlyWraps = items.length > 0 && items.every(_v2IsWrapLine);

  const cartItems = document.querySelector('cart-items');
  if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);
  const footer = document.getElementById('main-cart-footer');
  if (footer) footer.classList.toggle('is-empty', isEmpty);

  const checkout = document.querySelector('.cart__checkout-button');
  if (checkout) {
    const blocked = isEmpty || onlyWraps;
    checkout.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    checkout.classList.toggle('cart__checkout-button--disabled', blocked);
  }
};

const _v2ApplySections = (sections, cart) => {
  const drawer = document.querySelector('cart-drawer-v2');
  if (drawer && !_v2OnCartPage()) {
    drawer.renderContents({ sections: sections });
    return Promise.resolve();
  }

  const painted = _v2Repaint(sections);
  _v2SyncEmptyState(cart);

  // The bundled sections are the fast path; a GET against the real cart URL is
  // the reliable one. Only pay for it when the bundle let us down.
  if (!painted) {
    return _v2RefreshCartPage(cart).catch((err) => console.error('V2 cart page refresh error:', err));
  }
  return Promise.resolve();
};

// Repaints /cart from the server after a mutation whose response carried only
// drawer sections. Costs one extra round trip, which is why it is only used on
// paths that cannot bundle the cart page sections themselves.
async function _v2RefreshCartPage(knownCart) {
  const targets = _v2CartPageTargets();
  if (targets.length === 0) return;

  const [sections, cart] = await Promise.all([
    fetch(window.location.pathname + '?sections=' + targets.map((t) => t.section).join(',')).then((r) => r.json()),
    knownCart ? Promise.resolve(knownCart) : fetch('/cart.js').then((r) => r.json())
  ]);

  _v2Repaint(sections);
  _v2SyncEmptyState(cart);
}

async function _v2RefreshAll() {
  const ids = _v2AllSectionIds();
  if (ids.length === 0) return;
  const res = await fetch(window.location.pathname + '?sections=' + ids.join(','));
  _v2Repaint(await res.json());
}

const _v2ToggleCartPageLoader = (visible) => {
  const host = document.getElementById('main-cart-items');
  if (host) host.classList.toggle('cart-page-loader-visible', visible);
};

// cart.js dispatches cartChange while /cart/change.js is still open, so
// reconciling immediately reads the pre-change cart. enableLoading marks that
// window with .cart__items--disabled.
const _v2WaitForCartIdle = (timeoutMs = 4000) =>
  new Promise((resolve) => {
    const host = document.getElementById('main-cart-items');
    if (!host || !host.classList.contains('cart__items--disabled')) {
      resolve();
      return;
    }
    const startedAt = Date.now();
    const poll = setInterval(() => {
      if (!host.classList.contains('cart__items--disabled') || Date.now() - startedAt > timeoutMs) {
        clearInterval(poll);
        resolve();
      }
    }, 50);
  });

function _v2FindWraps(cartItems) {
  var wraps = {};
  cartItems.forEach(function(item) {
    if (item.properties && typeof item.properties['_gift_wrap'] === 'string' &&
        item.properties['_gift_wrap'].startsWith('v2_')) {
      wraps[item.properties['_gift_wrap']] = item;
    }
  });
  return wraps;
}

async function v2GiftWrapAdd(btn) {
  if (_v2GiftBusy) return;

  // Read the config before claiming the mutex: this ran outside the try below,
  // so a button rendered outside its section threw here and left _v2GiftBusy
  // stuck true, silently disabling every later reconcile.
  const section = btn.closest('[data-gift-wrap-section]');
  if (!section) {
    console.error('V2 gift wrap add: no [data-gift-wrap-section] ancestor.');
    return;
  }

  _v2GiftBusy = true;

  const giftMode = section.dataset.giftMode || 'paid';
  const paidVariantId = parseInt(section.dataset.giftPaidVariantId) || 0;
  const paidVariantPrice = section.dataset.giftPaidVariantPrice || '0';
  const freeVariantId = parseInt(section.dataset.giftFreeVariantId) || 0;
  const freeVariantPrice = section.dataset.giftFreeVariantPrice || '0';
  const loader = document.querySelector('.cart-loader-wrapper');
  if (loader) loader.classList.add('cart-loader-visible');
  _v2ToggleCartPageLoader(true);

  const giftTypeMap = _v2GetGiftTypeMap();

  try {
    const cartData = await fetch('/cart.js').then((r) => r.json());

    // A stale drawer render can still show the add button after a wrap was added
    // elsewhere; adding again merges into the existing line and doubles its quantity.
    if (cartData.items.some((item) => item.properties && item.properties['_gift_hide_quantity'])) {
      _v2GiftBusy = false;
      if (loader) loader.classList.remove('cart-loader-visible');
      _v2ToggleCartPageLoader(false);
      return;
    }

    let paidQty = 0;
    let freeQty = 0;
    let totalQty = 0;
    cartData.items.forEach((item) => {
      if (item.properties && item.properties['_gift_hide_quantity']) return;
      totalQty += item.quantity;
      const type = giftTypeMap[item.key];
      if (type === 'paid') paidQty += item.quantity;
      else if (type === 'free') freeQty += item.quantity;
    });

    if (totalQty === 0) { _v2GiftBusy = false; if (loader) loader.classList.remove('cart-loader-visible'); _v2ToggleCartPageLoader(false); return; }

    const itemsToAdd = [];
    let notePrice = '0';

    if (giftMode === 'combined') {
      // Paid and free wrap are the same variant: one merged line covers every wrappable item.
      const combinedVariantId = paidVariantId || freeVariantId;
      const combinedQty = paidQty + freeQty;
      if (combinedVariantId && combinedQty > 0) {
        itemsToAdd.push({ id: combinedVariantId, quantity: combinedQty, properties: { '_gift_wrap': 'v2_combined', '_gift_hide_quantity': true } });
        notePrice = paidVariantPrice || freeVariantPrice;
      }
    } else if (giftMode === 'mixed') {
      if (paidQty > 0 && paidVariantId) {
        itemsToAdd.push({ id: paidVariantId, quantity: paidQty, properties: { '_gift_wrap': 'v2_paid', '_gift_hide_quantity': true } });
        notePrice = paidVariantPrice;
      }
      if (freeQty > 0 && freeVariantId) {
        itemsToAdd.push({ id: freeVariantId, quantity: freeQty, properties: { '_gift_wrap': 'v2_free', '_gift_hide_quantity': true } });
      }
    } else if (giftMode === 'paid' && paidVariantId && paidQty > 0) {
      itemsToAdd.push({ id: paidVariantId, quantity: paidQty, properties: { '_gift_wrap': 'v2_combined', '_gift_hide_quantity': true } });
      notePrice = paidVariantPrice;
    } else if (giftMode === 'free' && freeVariantId && freeQty > 0) {
      itemsToAdd.push({ id: freeVariantId, quantity: freeQty, properties: { '_gift_wrap': 'v2_combined', '_gift_hide_quantity': true } });
      notePrice = freeVariantPrice;
    }

    if (itemsToAdd.length === 0) { _v2GiftBusy = false; if (loader) loader.classList.remove('cart-loader-visible'); _v2ToggleCartPageLoader(false); return; }

    const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: itemsToAdd,
        note: notePrice,
        attributes: { note: '| Gift wrap - True | Item Value - ' + notePrice },
        sections: _v2MutationSectionIds(),
        sections_url: window.location.pathname
      })
    });
    const data = await res.json();
    if (data.sections) {
      await _v2ApplySections(data.sections, data);
    } else {
      await _v2RefreshAll();
    }
  } catch (err) {
    console.error('V2 gift wrap add error:', err);
  }

  if (loader) loader.classList.remove('cart-loader-visible');
  _v2ToggleCartPageLoader(false);
  _v2GiftBusy = false;
}

async function v2GiftWrapRemove() {
  if (_v2GiftBusy) return;
  _v2GiftBusy = true;

  const loader = document.querySelector('.cart-loader-wrapper');
  if (loader) loader.classList.add('cart-loader-visible');
  _v2ToggleCartPageLoader(true);

  try {
    const cartData = await fetch('/cart.js').then((r) => r.json());

    const updates = {};
    cartData.items.forEach((item) => {
      if (item.properties && item.properties['_gift_hide_quantity']) {
        updates[item.key] = 0;
      }
    });

    if (Object.keys(updates).length) {
      const res = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: updates,
          note: '',
          attributes: { note: '' },
          sections: _v2MutationSectionIds(),
          sections_url: window.location.pathname
        })
      });
      const data = await res.json();
      if (data.sections) {
        await _v2ApplySections(data.sections, data);
      } else {
        await _v2RefreshAll();
      }
    } else {
      await _v2RefreshAll();
    }
  } catch (err) {
    console.error('V2 gift wrap remove error:', err);
  }

  if (loader) loader.classList.remove('cart-loader-visible');
  _v2ToggleCartPageLoader(false);
  _v2GiftBusy = false;
}

document.addEventListener('click', (e) => {
  const addBtn = e.target.closest('[data-action="add-gift-wrap"]');
  const removeBtn = e.target.closest('[data-action="remove-gift-wrap"]');

  if (addBtn) {
    var isModalBtn = addBtn.closest('[data-gift-modal]');
    if (isModalBtn) {
      var giftSection = addBtn.closest('[data-gift-wrap-section]');
      var giftMode = giftSection ? giftSection.dataset.giftMode : '';
      var paidVariantId = giftSection ? giftSection.dataset.giftPaidVariantId : '';
      var paidVariantPrice = giftSection ? giftSection.dataset.giftPaidVariantPrice : '';
      var freeVariantId = giftSection ? giftSection.dataset.giftFreeVariantId : '';
      var freeVariantPrice = giftSection ? giftSection.dataset.giftFreeVariantPrice : '';
      isModalBtn.setAttribute('aria-hidden', 'true');
      var fakeBtn = document.createElement('button');
      fakeBtn.setAttribute('data-action', 'add-gift-wrap');
      var fakeSection = document.createElement('div');
      fakeSection.setAttribute('data-gift-wrap-section', '');
      fakeSection.dataset.giftMode = giftMode;
      fakeSection.dataset.giftPaidVariantId = paidVariantId;
      fakeSection.dataset.giftPaidVariantPrice = paidVariantPrice;
      fakeSection.dataset.giftFreeVariantId = freeVariantId;
      fakeSection.dataset.giftFreeVariantPrice = freeVariantPrice;
      fakeSection.appendChild(fakeBtn);
      document.body.appendChild(fakeSection);
      v2GiftWrapAdd(fakeBtn);
      setTimeout(function() { fakeSection.remove(); }, 100);
    } else {
      v2GiftWrapAdd(addBtn);
    }
  }
  if (removeBtn) v2GiftWrapRemove();

  var openGiftModal = e.target.closest('[data-action="open-gift-modal"]');
  if (openGiftModal) {
    var giftSection = openGiftModal.closest('[data-gift-wrap-section]');
    if (giftSection) {
      var modal = giftSection.querySelector('[data-gift-modal]');
      if (modal) modal.setAttribute('aria-hidden', 'false');
    }
  }

  var closeGiftModal = e.target.closest('[data-action="close-gift-modal"]');
  if (closeGiftModal) {
    var modal = closeGiftModal.closest('[data-gift-modal]');
    if (modal) modal.setAttribute('aria-hidden', 'true');
  }
});

let _v2ReconcileRunning = false;
let _v2ReconcilePending = false;

// One serialized path for every wrap behaviour (merge, split, quantity
// reconcile, backfill). A request arriving mid-run re-runs the loop instead of
// being dropped — dropping it was why the cart only resynced on the next
// interaction.
function _v2QueueReconcile() {
  if (!document.querySelector('cart-drawer-v2')) return;
  _v2GetGiftTypeMap();
  _v2ReconcilePending = true;
  if (_v2ReconcileRunning) return;
  _v2DrainReconcile();
}

async function _v2DrainReconcile() {
  _v2ReconcileRunning = true;
  _v2ToggleCartPageLoader(true);
  try {
    while (_v2ReconcilePending) {
      _v2ReconcilePending = false;
      await _v2WaitForCartIdle();
      await _v2Reconcile();
    }
  } finally {
    _v2ReconcileRunning = false;
    _v2ToggleCartPageLoader(false);
  }
}

async function _v2Reconcile() {
  if (_v2GiftBusy) return;

  try {
    const cartData = await fetch('/cart.js').then((r) => r.json());
    const v2Wraps = _v2FindWraps(cartData.items);

    if (Object.keys(v2Wraps).length === 0) return;

    const giftTypeMap = _v2GetGiftTypeMap();

    // Without a rendered row for every line we cannot tell paid from free, and
    // guessing would set the wrap too low. Bail rather than write a bad quantity.
    if (cartData.items.some((item) => !_v2IsWrapLine(item) && giftTypeMap[item.key] === undefined)) return;

    let paidQty = 0;
    let freeQty = 0;

    for (const item of cartData.items) {
      if (_v2IsWrapLine(item)) continue;
      const type = giftTypeMap[item.key];
      if (type === 'paid') paidQty += item.quantity;
      else if (type === 'free') freeQty += item.quantity;
    }

    const section = document.querySelector('[data-gift-wrap-section]');
    const giftMode = section ? section.dataset.giftMode : '';

    // Paid and free wrap are the same variant: collapse any split pair back into one line.
    if (giftMode === 'combined' && (v2Wraps['v2_paid'] || v2Wraps['v2_free'])) {
      const mergeUpdates = {};
      ['v2_paid', 'v2_free', 'v2_combined'].forEach((wrapType) => {
        if (v2Wraps[wrapType]) mergeUpdates[v2Wraps[wrapType].key] = 0;
      });
      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: mergeUpdates })
      });

      const mergedVariantId = parseInt(section.dataset.giftPaidVariantId) || parseInt(section.dataset.giftFreeVariantId);
      const mergedQty = paidQty + freeQty;
      if (mergedVariantId && mergedQty > 0) {
        await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: mergedVariantId, quantity: mergedQty, properties: { '_gift_wrap': 'v2_combined', '_gift_hide_quantity': true } }] })
        });
      }

      await _v2RefreshAll();
      _v2SyncEmptyState(await fetch('/cart.js').then((r) => r.json()));
      return;
    }

    if (v2Wraps['v2_combined'] && paidQty > 0 && freeQty > 0 && section && giftMode === 'mixed') {
      const removeUpdates = { [v2Wraps['v2_combined'].key]: 0 };
      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: removeUpdates })
      });

      const newWraps = [];
      const paidVid = parseInt(section.dataset.giftPaidVariantId);
      const freeVid = parseInt(section.dataset.giftFreeVariantId);
      if (paidVid) newWraps.push({ id: paidVid, quantity: paidQty, properties: { '_gift_wrap': 'v2_paid', '_gift_hide_quantity': true } });
      if (freeVid) newWraps.push({ id: freeVid, quantity: freeQty, properties: { '_gift_wrap': 'v2_free', '_gift_hide_quantity': true } });

      if (newWraps.length > 0) {
        await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: newWraps })
        });
      }

      await _v2RefreshAll();
      _v2SyncEmptyState(await fetch('/cart.js').then((r) => r.json()));
      return;
    }

    const targetQtys = { 'v2_combined': paidQty + freeQty, 'v2_paid': paidQty, 'v2_free': freeQty };
    const wrapUpdates = {};
    for (const wrapType in v2Wraps) {
      const target = targetQtys[wrapType] || 0;
      if (v2Wraps[wrapType].quantity !== target) {
        wrapUpdates[v2Wraps[wrapType].key] = target;
      }
    }

    const isPerTypeMode = (v2Wraps['v2_paid'] || v2Wraps['v2_free']) && giftMode !== 'combined';
    const missingWraps = [];
    if (isPerTypeMode && section) {
      if (paidQty > 0 && !v2Wraps['v2_paid']) {
        const vid = parseInt(section.dataset.giftPaidVariantId);
        if (vid) missingWraps.push({ id: vid, quantity: paidQty, properties: { '_gift_wrap': 'v2_paid', '_gift_hide_quantity': true } });
      }
      if (freeQty > 0 && !v2Wraps['v2_free']) {
        const vid = parseInt(section.dataset.giftFreeVariantId);
        if (vid) missingWraps.push({ id: vid, quantity: freeQty, properties: { '_gift_wrap': 'v2_free', '_gift_hide_quantity': true } });
      }
    }

    if (Object.keys(wrapUpdates).length === 0 && missingWraps.length === 0) return;

    if (missingWraps.length > 0) {
      await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: missingWraps })
      });
    }

    if (Object.keys(wrapUpdates).length > 0) {
      const res = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: wrapUpdates,
          sections: _v2MutationSectionIds(),
          sections_url: window.location.pathname
        })
      });
      const data = await res.json();
      if (data.sections) {
        await _v2ApplySections(data.sections, data);
      } else {
        await _v2RefreshAll();
        _v2SyncEmptyState(data);
      }
    } else {
      await _v2RefreshAll();
      _v2SyncEmptyState(await fetch('/cart.js').then((r) => r.json()));
    }
  } catch (err) {
    console.error('V2 gift reconcile error:', err);
  }
}

document.addEventListener('cart:qty:change', _v2QueueReconcile);
document.addEventListener('cartChange', _v2QueueReconcile);

// A cart mutated elsewhere (checkout, another tab) should be corrected on the
// first paint of /cart rather than on the next interaction. Guard the
// readyState: this file is deferred today, but the theme also has an ajax page
// loader that can evaluate scripts after the event has already fired.
const _v2ReconcileOnLoad = () => {
  // Only /cart needs this: everywhere else the drawer reconciles when it opens,
  // so reconciling here would cost every page view a /cart.js round trip.
  if (_v2OnCartPage()) _v2QueueReconcile();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _v2ReconcileOnLoad);
} else {
  _v2ReconcileOnLoad();
}

class CartDrawerV2Items extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawerV2',
        section: 'cart-drawer-v2',
        selector: '.cart-drawer-v2-panel'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-v2-items', CartDrawerV2Items);

function cartBestsellerQuickAdd(productId) {
  if (window.innerWidth <= 1024) {
    qAtcMobOpenModel(productId);
    return;
  }

  var overlay = document.querySelector('.cart-drawer-v2-qa-overlay');
  var drawer = document.querySelector('.cart-drawer-v2-qa-drawer');
  if (!overlay || !drawer) return;

  var productData = window.q_atc_products[productId];
  var productOptions = window.q_atc_product_options[productId];
  if (!productData) return;

  var imgWrap = drawer.querySelector('.cart-drawer-v2-qa-img-wrap');
  var imageSrc = productData.featured_image || null;
  imgWrap.innerHTML = imageSrc ? '<img src="' + imageSrc + '&width=200" class="q-atc-mob-product-image"/>' : '';

  var infoWrap = drawer.querySelector('.cart-drawer-v2-qa-info-wrap');
  infoWrap.innerHTML =
    '<h5 class="q-atc-mob-product-title">' + productData.title + '</h5>' +
    '<div class="q-atc-mob-product-price-wrapper">' +
      '<span class="q-atc-mob-compare-at-price"><s>' + (productData.compare_at_price ? paisaToInr(productData.compare_at_price) : '') + '</s></span>' +
      '<span class="q-atc-mob-price">' + paisaToInr(productData.price) + '</span>' +
    '</div>';

  var variantsWrap = drawer.querySelector('.cart-drawer-v2-qa-variants-wrap');
  var optionsHtml = '';

  if (productOptions !== 'default') {
    optionsHtml += '<div class="q-atc-mob-variant-option-container" data-options-count="' + productOptions.length + '" data-product-id="' + productId + '">';

    for (var i = 0; i < productOptions.length; i++) {
      optionsHtml += '<div class="q-atc-mob-variant-option" data-option-name="' + productOptions[i].name + '" data-option-position="' + productOptions[i].position + '">';
      optionsHtml += '<h3 class="q-atc-mob-option-title">' + productOptions[i].name + '</h3>';
      optionsHtml += '<div class="q-atc-mob-option-values-wrapper">';

      for (var j = 0; j < productOptions[i].values.length; j++) {
        var val = productOptions[i].values[j];
        var inputId = 'option-m-' + productId + '-' + productOptions[i].name.toLowerCase().replace(' ', '_') + '-' + val.toLowerCase().replace(' ', '_');
        var checked = (productOptions[i].name !== 'Size' && productOptions[i].name !== 'size' && j === 0) ? ' checked' : '';

        optionsHtml += '<input style="display:none;" onchange="qAtcVariantOnchange(this,false)" type="radio" class="q-atc-mob-input" data-option-position="' + productOptions[i].position + '" value="' + val + '" data-option-name="' + productOptions[i].name + '" id="' + inputId + '" name="option-m-' + productId + '-' + productOptions[i].position + '"' + checked + '>';
        optionsHtml += '<label class="q-atc-mob-label" for="' + inputId + '" data-option-name="' + productOptions[i].name.toLowerCase() + '" data-option-value>' + val + '</label>';
      }

      optionsHtml += '</div></div>';
    }
    optionsHtml += '</div>';
  }
  variantsWrap.innerHTML = optionsHtml;

  var atcBtn = drawer.querySelector('.cart-drawer-v2-qa-atc-btn');
  if (productOptions === 'default') {
    atcBtn.setAttribute('data-default-product', 'true');
    atcBtn.setAttribute('data-selected-variant', productData.variants[0].id);
    atcBtn.disabled = !productData.variants[0].available;
    atcBtn.textContent = productData.variants[0].available ? 'Add To Cart' : 'Sold Out';
  } else {
    atcBtn.setAttribute('data-default-product', 'false');
    atcBtn.setAttribute('data-selected-variant', '');
    atcBtn.disabled = true;
    atcBtn.textContent = 'Add To Cart';
  }

  if (productOptions !== 'default') {
    var optionSelectors = drawer.querySelectorAll('.q-atc-mob-variant-option');
    var firstVariant = productData.variants[0];

    var applyAvailability = function(selector, valueIndex, available, hasCombination) {
      var optionValue = Array.from(selector.querySelectorAll('[data-option-value]'))[valueIndex];
      if (!optionValue) return;
      optionValue.toggleAttribute('hidden', !hasCombination);
      optionValue.classList.toggle('is-disabled', !available);
    };

    if (optionSelectors[0]) {
      productOptions[0].values.forEach(function(value, idx) {
        var hasCombination = productData.variants.some(function(v) { return v.option1 === value; });
        var hasAvailable = productData.variants.some(function(v) { return v.option1 === value && v.available; });
        applyAvailability(optionSelectors[0], idx, hasAvailable, hasCombination);

        if (optionSelectors[1]) {
          productOptions[1].values.forEach(function(value2, idx2) {
            var hasCombination2 = productData.variants.some(function(v) { return v.option2 === value2 && v.option1 === firstVariant.option1; });
            var hasAvailable2 = productData.variants.some(function(v) { return v.option2 === value2 && v.option1 === firstVariant.option1 && v.available; });
            applyAvailability(optionSelectors[1], idx2, hasAvailable2, hasCombination2);

            if (optionSelectors[2]) {
              productOptions[2].values.forEach(function(value3, idx3) {
                var hasCombination3 = productData.variants.some(function(v) { return v.option3 === value3 && v.option1 === firstVariant.option1 && v.option2 === firstVariant.option2; });
                var hasAvailable3 = productData.variants.some(function(v) { return v.option3 === value3 && v.option1 === firstVariant.option1 && v.option2 === firstVariant.option2 && v.available; });
                applyAvailability(optionSelectors[2], idx3, hasAvailable3, hasCombination3);
              });
            }
          });
        }
      });
    }
  }

  overlay.classList.add('is-open');
  drawer.classList.add('is-open');
}

function cartBestsellerQaClose() {
  var overlay = document.querySelector('.cart-drawer-v2-qa-overlay');
  var drawer = document.querySelector('.cart-drawer-v2-qa-drawer');
  if (overlay) overlay.classList.remove('is-open');
  if (drawer) drawer.classList.remove('is-open');
}

document.addEventListener('click', function(e) {
  if (e.target.closest('.cart-drawer-v2-qa-close')) {
    cartBestsellerQaClose();
  }
  if (e.target.classList.contains('cart-drawer-v2-qa-overlay')) {
    cartBestsellerQaClose();
  }
  if (e.target.closest('.cart-drawer-v2-qa-atc-btn')) {
    var btn = e.target.closest('.cart-drawer-v2-qa-atc-btn');
    qAtcProductAddToCart(btn);
  }
  var qaBtnEl = e.target.closest('.cart-drawer-v2-bestseller-quick-add');
  if (qaBtnEl && !qaBtnEl.classList.contains('is_disabled')) {
    e.preventDefault();
    e.stopPropagation();
    var productId = qaBtnEl.getAttribute('data-bestseller-qa-id');
    if (productId) cartBestsellerQuickAdd(productId);
  }
});

function initBestsellersScrollbar() {
  const grid = document.querySelector('.cart-drawer-v2-bestsellers-grid');
  const thumb = document.querySelector('.cart-drawer-v2-bestsellers-scrollbar-thumb');
  if (!grid || !thumb) return;

  const minFill = 15;

  const updateProgress = () => {
    const maxScroll = grid.scrollWidth - grid.clientWidth;
    if (maxScroll <= 0) {
      thumb.style.width = minFill + '%';
      return;
    }
    const progress = minFill + (grid.scrollLeft / maxScroll) * (100 - minFill);
    thumb.style.width = progress + '%';
  };

  grid.addEventListener('scroll', updateProgress, { passive: true });
  updateProgress();
}

initBestsellersScrollbar();
document.addEventListener('cartChange', () => {
  setTimeout(initBestsellersScrollbar, 100);
});

// Set by referral-page.js when a logged-in shopper successfully claims a referral.
// The class goes on <body> because #CartDrawerV2's innerHTML is replaced wholesale on every
// cart mutation (renderContents), so anything inside the drawer would not survive.
function applyReferralClaimedState() {
  try {
    if (sessionStorage.getItem('referral_claimed')) {
      document.body.classList.add('referral-claimed');
    }
  } catch (e) {}
}

applyReferralClaimedState();
